import { useEffect } from 'react';

/**
 * useBodyScrollLock(active)
 * ----------------------------------------------------------------------------
 * When `active` is truthy, locks the page background from scrolling. Used by
 * floating viewers (blog modal, event modal, code viewer, forum thread
 * popup, etc.) so the user can only scroll the modal content, not the page
 * behind it.
 *
 * Implementation notes:
 *  - We pin <html>/<body> with `overflow: hidden` + compensate the scrollbar
 *    width via `padding-right` so the page doesn't "jump" sideways when the
 *    gutter disappears (common gotcha with naïve overflow:hidden locks).
 *  - Uses a ref-counting scheme via a counter on the document so multiple
 *    concurrently open modals still release the lock correctly.
 *  - Restores the previous inline styles on cleanup so we don't clobber any
 *    unrelated body styling set by other components.
 */
const LOCK_COUNT_KEY = '__ispLockCount';
const LOCK_ORIG_KEY  = '__ispLockOrig';

export default function useBodyScrollLock(active) {
  useEffect(() => {
    if (!active) return undefined;
    const html = document.documentElement;
    const body = document.body;

    if (!window[LOCK_COUNT_KEY]) {
      // First locker — snapshot original styles and apply the lock.
      const scrollbarW = window.innerWidth - html.clientWidth;
      window[LOCK_ORIG_KEY] = {
        htmlOverflow: html.style.overflow,
        bodyOverflow: body.style.overflow,
        bodyPaddingRight: body.style.paddingRight
      };
      html.style.overflow = 'hidden';
      body.style.overflow = 'hidden';
      if (scrollbarW > 0) body.style.paddingRight = `${scrollbarW}px`;
      window[LOCK_COUNT_KEY] = 1;
    } else {
      window[LOCK_COUNT_KEY] += 1;
    }

    return () => {
      window[LOCK_COUNT_KEY] -= 1;
      if (window[LOCK_COUNT_KEY] <= 0) {
        const orig = window[LOCK_ORIG_KEY] || {};
        html.style.overflow = orig.htmlOverflow || '';
        body.style.overflow = orig.bodyOverflow || '';
        body.style.paddingRight = orig.bodyPaddingRight || '';
        delete window[LOCK_COUNT_KEY];
        delete window[LOCK_ORIG_KEY];
      }
    };
  }, [active]);
}
